import { BadStateError } from '@skand/utils';
import { atom } from 'jotai';
import ky, { DownloadProgress } from 'ky';
import { logger } from '../../logger';
import { JotaiStore } from '../../stores/jotaiStore';
import { DownloadContext } from '../DownloadContext';
import { Progress } from '../Progress/Progress';
import { isAbortionError } from '../errors';
import { DownloaderStage, IDownloader } from './IDownloader';
import { queue } from './queue';

export class Downloader implements IDownloader {
  private _downloadPromise: null | Promise<Blob> = null;
  private _progress: Progress;
  private _store: JotaiStore;
  readonly range: null | [number, number] = null;
  readonly url: string;

  private _dataAtom = atom<null | Blob>(null);
  private _errorAtom = atom<null | unknown>(null);
  private _stageAtom = atom<DownloaderStage>('initial');

  readonly dataAtom = atom(get => get(this._dataAtom));
  readonly errorAtom = atom(get => get(this._errorAtom));
  readonly stageAtom = atom(get => get(this._stageAtom));

  constructor(args: {
    url: string;
    contentLength?: number;
    downloadContext: DownloadContext;
    range?: [number, number];
  }) {
    this._progress = new Progress({
      bytesTotal: args.contentLength,
      downloadContext: args.downloadContext,
    });
    this._store = args.downloadContext.jotaiStore;
    this.range = args.range ?? null;
    this.url = args.url;
  }

  async start(signal?: AbortSignal) {
    const currentStage = this._store.get(this._stageAtom);

    if (currentStage === 'aborted' || currentStage === 'failure') {
      // retry
      this._downloadPromise = null;
      this._progress.reset();
      this._store.set(this._dataAtom, null);
      this._store.set(this._errorAtom, null);
      this._store.set(this._stageAtom, 'initial');
    }

    if (this._downloadPromise === null) {
      this._downloadPromise = this._download(signal);
      this._store.set(this._stageAtom, 'downloading');
    } else if (signal) {
      logger.warn('signal is ignored because the download has already been started');
    }

    try {
      const blob = await this._downloadPromise;
      return blob;
    } catch (error) {
      this._store.set(this._errorAtom, error);

      if (isAbortionError(error)) {
        logger.warn(error);
        this._store.set(this._stageAtom, 'aborted');
      } else {
        this._store.set(this._stageAtom, 'failure');
      }

      throw error;
    }
  }

  private _onProgress = (kyProgress: DownloadProgress) => {
    this._progress.update(kyProgress.transferredBytes, kyProgress.totalBytes);
  };

  private async _download(signal?: AbortSignal) {
    const downloadFn = async () => {
      const headers = this.range ? { Range: `bytes=${this.range[0]}-${this.range[1]}` } : undefined;

      const response = await ky(this.url, {
        method: 'GET',
        onDownloadProgress: this._onProgress,
        retry: 0, // disable retry
        signal,
        timeout: false, // disable auto-timeout
        headers,
      });

      const blob = await response.blob();
      return blob;
    };

    const blob = await queue.add(downloadFn, { signal });
    // queue.add returns void when the task timed out. We know this is not our case.
    // We can safely assume that the blob is defined.
    // @see https://github.com/sindresorhus/p-queue/blob/main/source/index.ts#L236
    if (!blob) throw new BadStateError('invalid blob');

    this.progress.finish();
    this._store.set(this._dataAtom, blob);
    this._store.set(this._stageAtom, 'success');

    return blob;
  }

  get progress() {
    return this._progress;
  }
}
